<template>
  <div class="w-full mx-auto sm:max-w-5xl 2xl:max-w-6xl 3xl:max-w-8xl 4xl:max-w-9xl">
    <router-view />
  </div>
</template>

<script lang="ts" setup>
import { camelCase } from '@replygirl/change-case-object';
import { useCampaignModals } from '@/composables/useCampaignModals';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useUserStore } from '@/store/useUserStore';
import { useCampaignModalsStore, CampaignModalTarget } from '@/store/useCampaignModalsStore';
import { useAiMetadataStore } from './store/useAiMetadataStore';
import { useArtifactsStore } from './store/useArtifactsStore';
import { useSmartActionsStore } from './store/useSmartActionsStore';
import { useTranscriptionStore, ItemTranscriptionStatus } from './store/useTranscriptionStore';
import { useViewerItemStore } from './store/useViewerItemStore';
import { useViewerModals } from '@/modules/viewer/composables/useViewerModals';
import { watch, onMounted, onUnmounted, inject, onBeforeMount } from 'vue';
import { storeToRefs } from 'pinia';
import { PusherPlugin } from '@/plugins/pusher';
import { useCollectionsStore } from '@/store/useCollectionsStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { useFirstItemModal } from './composables/useFirstItemModal';
import { useCallbacksStorage } from './composables/useCallbacksStorage';

const campaignModals = useCampaignModals();
const aiMetadataStore = useAiMetadataStore();
const artifactsStore = useArtifactsStore();
const smartActionsStore = useSmartActionsStore();
const transcriptionStore = useTranscriptionStore();
const store = useViewerItemStore();
const viewerModals = useViewerModals();
const layoutStore = useLayoutStore();
const userStore = useUserStore();
const campaignModalsStore = useCampaignModalsStore();
const collectionsStore = useCollectionsStore();
const entitlementsStore = useEntitlementsStore();

const pusher = inject<PusherPlugin>('$pusher');
const CHANNEL_ITEM_UPDATE_EVENT_NAME = 'item_update';
const CHANNEL_CAPTION_FILE_GENERATED = 'caption_file_generated';
const CHANNEL_TRANSCRIPTION_UPDATED_EVENT_NAME = 'transcription_updated';
const CHANNEL_AI_METADATA_UPDATE_EVENT_NAME = 'ai_metadata_update';
const CHANNEL_AI_SMART_ACTION_UPDATE_EVENT_NAME = 'ai_smart_action_update';
const CHANNEL_ENTITLEMENT_QUOTA_UPDATED_EVENT_NAME = 'entitlement_quota_updated';
const CHANNEL_OCR_RESULT_UPDATE_EVENT_NAME = 'ocr_result_update';

const { id } = storeToRefs(store);
const { itemCount } = storeToRefs(userStore);

const initializePusher = (id?: string) => {
  if (!id || !pusher) return;

  pusher.subscribeToItem(id);
  bindItemPusherEvents();
  bindUserPusherEvents();
};
const loadCampaignModals = () => userStore.isLoggedIn && campaignModalsStore.load(CampaignModalTarget.VIEWER);
const runItemCallbacks = (id?: string) => {
  if (!id) {
    return;
  }
  useCallbacksStorage().run(id);
};

const onQuotaUpdated = (payload: any) => {
  const data = camelCase(payload?.data);
  const attrs = data?.usagequota?.attributes;
  if (attrs?.entitlementType === 'smart_actions') {
    entitlementsStore.$patch(state => {
      state.quotas.smartActions = {
        used: attrs.quota.used,
        remaining: attrs.quota.remaining,
        expiresAt: attrs.quota.expiresAt,
      };
    });
  }
};

const loadFirstItemModal = () => {
  if (itemCount && itemCount.value === 1) {
    const { open: openFirstItemModal } = useFirstItemModal();
    openFirstItemModal();
  }
};

watch(id, itemId => {
  initializePusher(itemId);
  runItemCallbacks(itemId);
});

onUnmounted(() => {
  layoutStore.hideCta();
  layoutStore.showBackButton();

  if (!pusher) return;
  pusher.unbindAllItemChannel();
  pusher.unsubscribeFromItem();
  pusher.unbindUser(CHANNEL_ENTITLEMENT_QUOTA_UPDATED_EVENT_NAME, onQuotaUpdated);
});

onBeforeMount(async () => {
  layoutStore.showCta();
  layoutStore.hideBackButton();
  collectionsStore.load();

  initializePusher(id.value);

  /**
   * @todo remove after resolving [SW-3059] issue
   */
  store.preloadPendingItem();
});

onMounted(() => {
  loadCampaignModals();
  loadFirstItemModal();

  // show modals based on URL query params
  viewerModals.openModalFromUrlQuery();

  // track custom domains view
  store.trackCustomDomainView();

  // run stored callbacks on init
  runItemCallbacks(id.value);
});

const bindItemPusherEvents = () => {
  if (!pusher) return;
  pusher.unbindAllItemChannel();

  pusher.bindItem(CHANNEL_ITEM_UPDATE_EVENT_NAME, onItemMessage);
  pusher.bindItem(CHANNEL_CAPTION_FILE_GENERATED, onItemMessage);
  pusher.bindItem(CHANNEL_AI_METADATA_UPDATE_EVENT_NAME, (payload: any) => {
    const data = camelCase(payload?.data).itemaimetadata;
    aiMetadataStore.patchAIMetaData(data);
  });
  pusher.bindItem(CHANNEL_OCR_RESULT_UPDATE_EVENT_NAME, (payload: any) => {
    const artifactData = camelCase(payload?.data)['artifacts/ocr'];
    if (artifactData) {
      artifactsStore.upsertArtifact({
        id: artifactData.id,
        type: artifactData.attributes?.type,
        status: artifactData.attributes?.status,
        createdAt: artifactData.attributes?.createdAt,
        updatedAt: artifactData.attributes?.updatedAt,
        content: artifactData.attributes?.content,
        publicBucketUrls: artifactData.attributes?.publicBucketUrls || [],
        itemId: artifactData.relationships?.item?.data?.id || '',
      });
    }
  });
  pusher.bindItem(CHANNEL_AI_SMART_ACTION_UPDATE_EVENT_NAME, (payload: any) => {
    const data = camelCase(payload?.data).aismartaction;
    smartActionsStore.patchSmartActionsData(data);
  });
  pusher.bindItem(CHANNEL_TRANSCRIPTION_UPDATED_EVENT_NAME, (payload: any) => {
    const status = payload?.data?.transcription?.attributes.status;

    // we don't have full transcription data coming from pusher so we need to make request for them - see serializers
    if (status === ItemTranscriptionStatus.DONE) {
      transcriptionStore.loadTranscription(transcriptionStore.transcriptionId);
    } else {
      transcriptionStore.populateTranscriptionData(payload?.data);
    }
  });
};

const bindUserPusherEvents = () => {
  if (!pusher) return;

  pusher.bindUser(CHANNEL_ENTITLEMENT_QUOTA_UPDATED_EVENT_NAME, onQuotaUpdated);
};

// fot now we leave it here - but we need to move it to store or composable
const onItemMessage = (payload: any) => {
  if (!payload || !payload.data || !payload.data.item) {
    return;
  }

  const store = useViewerItemStore();
  store.onPusherUpdate(camelCase(payload.data).item);
};

watch(
  () => userStore.isLoggedIn,
  () => loadCampaignModals()
);

watch(
  () => campaignModalsStore.showCampaignModal,
  () => {
    if (campaignModalsStore.showCampaignModal) {
      return campaignModals.openCampaignModal();
    }
  }
);
</script>
